/**
 * SEO Anchor Variations — ElevAdvisor
 *
 * Resolve canibalização de anchors internos (Seobility: 57 páginas com anchor texts idênticos).
 *
 * Cada URL interna tem um conjunto de anchors variantes semanticamente equivalentes.
 * Ao linkar internamente, usar `pickAnchor(url, seed?)` em vez de uma string fixa.
 *
 * Regras:
 * - Nunca repetir o mesmo anchor duas vezes na mesma página (passar `usedAnchors` como seed).
 * - Manter pelo menos 1 variante com a keyword exata (para preservar relevância).
 * - Variantes adicionais devem ser sinónimos verbais ou parafraseamentos naturais.
 */

export const ANCHOR_VARIATIONS: Record<string, string[]> = {
  // ═══════════════════════════════════════════════════════════════════
  // CLUSTER 1 — NÚCLEO COMERCIAL (Auditoria & Plataforma)
  // ═══════════════════════════════════════════════════════════════════
  '/auditoria-elevadores': [
    'auditoria técnica de elevadores',
    'pedir auditoria independente',
    'avaliação técnica imparcial',
    'consultoria técnica especializada',
    'parecer técnico independente',
    'engenharia de elevadores independente',
    'auditoria de elevadores',
    'verificar estado do elevador',
    'análise técnica do elevador',
    'diagnóstico do elevador',
    'inspecionar elevador de forma independente',
    'avaliação imparcial do equipamento',
  ],

  '/auditoria-elevadores-condominio': [
    'auditoria de elevadores em condomínio',
    'análise técnica para condomínios',
    'diagnóstico do elevador do prédio',
    'avaliação independente do elevador do condomínio',
    'apoio técnico ao administrador',
  ],
  '/avaliacao-tecnica-elevador': [
    'avaliação técnica do elevador',
    'estado real do equipamento',
    'peritagem técnica ao elevador',
    'verificar condições do elevador',
    'análise de desempenho do elevador',
  ],
  '/diagnostico-elevadores': [
    'diagnóstico técnico de elevadores',
    'identificar causas de avarias',
    'análise de falhas do elevador',
    'pré-diagnóstico do equipamento',
    'avaliar problemas recorrentes',
  ],
  '/diagnostico-elevador': [
    'diagnóstico do elevador',
    'identificar causa raiz da avaria',
    'leitura técnica do equipamento',
    'análise de falhas no elevador',
  ],



  // ═══════════════════════════════════════════════════════════════════
  // CLUSTER 2 — CONSULTORIA & ANÁLISE
  // ═══════════════════════════════════════════════════════════════════
  '/consultoria-elevadores': [
    'consultoria técnica de elevadores',
    'apoio à decisão em elevadores',
    'consultor independente de elevadores',
    'aconselhamento técnico especializado',
    'consultoria EN 81',
  ],
  '/consultoria-tecnica-elevadores': [
    'consultoria técnica especializada',
    'engenharia consultiva de elevadores',
    'apoio à decisão técnica',
    'parecer de consultoria',
  ],
  '/consultor-elevadores-condominio': [
    'consultor de elevadores para condomínios',
    'apoio ao administrador de condomínio',
    'consultoria condominial de elevadores',
    'engenheiro de elevadores ao serviço do condomínio',
  ],
  '/analise-tecnica-elevadores': [
    'análise técnica de elevadores',
    'estudo de engenharia ao elevador',
    'avaliação técnica detalhada',
    'matriz de risco do equipamento',
    'estudo técnico independente',
  ],
  '/segunda-opiniao-tecnica-elevadores': [
    'segunda opinião técnica',
    'parecer independente complementar',
    'validar relatório técnico',
    'contraprova de engenharia',
  ],
  '/planos-consultoria': [
    'planos de consultoria técnica',
    'pacotes de apoio técnico',
    'modalidades de consultoria',
  ],
  '/chamada-diagnostico': [
    'chamada de diagnóstico gratuita',
    'sessão de pré-diagnóstico',
    'reunião técnica inicial',
  ],

  // ═══════════════════════════════════════════════════════════════════
  // CLUSTER 3 — FERRAMENTAS INTERATIVAS
  // ═══════════════════════════════════════════════════════════════════
  '/ferramentas-elevadores': [
    'ferramentas técnicas de elevadores',
    'utilitários de diagnóstico',
    'simuladores e calculadoras',
    'apps de apoio à decisão',
    'recursos interativos para elevadores',
  ],
  '/simulador-risco-elevador': [
    'simulador de risco do elevador',
    'avaliar risco técnico',
    'calcular nível de risco',
    'pré-diagnóstico de risco online',
    'estimar risco operacional',
  ],
  '/calculadora-modernizacao-elevador': [
    'calculadora de modernização',
    'estimar custo de modernização',
    'simular modernização do elevador',
    'cenários de investimento em modernização',
    'planeamento financeiro de modernização',
  ],
  '/analise-contrato-elevador': [
    'analisador de contrato de manutenção',
    'analisar contrato do elevador',
    'verificar contrato de manutenção',
    'leitura inteligente de cláusulas',
    'auditar contrato online',
  ],
  '/calculadora-consumo-elevador': [
    'calculadora de consumo energético',
    'estimar consumo do elevador',
    'simular eficiência energética',
    'medir gasto energético do elevador',
  ],

  // ═══════════════════════════════════════════════════════════════════
  // CLUSTER 4 — CONTRATOS DE MANUTENÇÃO
  // ═══════════════════════════════════════════════════════════════════
  '/contrato-manutencao-elevadores': [
    'contrato de manutenção de elevadores',
    'guia de contratos de manutenção',
    'cláusulas de manutenção',
    'tipos de contrato de elevadores',
    'modelo de contrato com EMIE',
  ],
  '/analise-contrato-manutencao-elevador': [
    'análise de contrato de manutenção',
    'revisão técnica do contrato',
    'auditoria contratual ao elevador',
    'parecer sobre contrato com manutentor',
  ],
  '/auditoria-contrato-manutencao-elevadores': [
    'auditoria ao contrato de manutenção',
    'verificação independente do contrato',
    'análise contratual aprofundada',
  ],
  '/clausulas-abusivas-contrato-manutencao-elevador': [
    'cláusulas abusivas em contratos de elevadores',
    'identificar cláusulas problemáticas',
    'cláusulas de risco no contrato',
    'detectar abusos contratuais',
    'cláusulas desfavoráveis ao condomínio',
  ],

  // ═══════════════════════════════════════════════════════════════════
  // CLUSTER 5 — NORMAS & SEGURANÇA
  // ═══════════════════════════════════════════════════════════════════
  '/normas-transporte-vertical-portugal': [
    'normas de transporte vertical',
    'regulamentação EN 81',
    'normativa técnica em Portugal',
    'enquadramento normativo de elevadores',
    'legislação aplicável a elevadores',
  ],
  '/seguranca-elevadores': [
    'segurança de elevadores',
    'requisitos de segurança',
    'componentes de segurança',
    'normas de segurança EN 81',
    'segurança operacional do elevador',
  ],
  '/inspecao-elevadores': [
    'inspeção periódica de elevadores',
    'inspeção regulamentar',
    'verificação técnica obrigatória',
    'inspeção EN 81 obrigatória',
    'auditoria regulamentar ao elevador',
  ],

  // ═══════════════════════════════════════════════════════════════════
  // CLUSTER 6 — GESTÃO PREDIAL & RESIDENCIAL
  // ═══════════════════════════════════════════════════════════════════
  '/gestao-predial-elevadores': [
    'gestão predial de elevadores',
    'administração técnica do elevador',
    'gestão técnica do edifício',
    'apoio à gestão de imóveis',
    'manutenção predial de elevadores',
  ],
  '/guia-elevador-residencial-portugal-2026': [
    'guia do elevador residencial',
    'manual técnico do elevador',
    'guia 2026 de elevadores residenciais',
    'guia completo do proprietário',
    'referência técnica residencial',
  ],

  // ═══════════════════════════════════════════════════════════════════
  // CLUSTER 7 — MODERNIZAÇÃO & PROBLEMAS TÉCNICOS
  // ═══════════════════════════════════════════════════════════════════
  '/modernizacao-elevadores': [
    'modernização de elevadores',
    'renovar elevador antigo',
    'upgrade técnico do equipamento',
    'projeto de modernização',
    'atualização de elevadores',
  ],
  '/modernizacao-elevadores-guia': [
    'guia de modernização de elevadores',
    'planear modernização do elevador',
    'roadmap de upgrade técnico',
    'decidir investir em modernização',
  ],
  '/manutencao-elevadores': [
    'manutenção de elevadores',
    'serviços de manutenção técnica',
    'práticas de manutenção EN 81',
    'gestão da manutenção do elevador',
  ],
  '/problemas-elevadores': [
    'problemas comuns em elevadores',
    'avarias frequentes do elevador',
    'falhas técnicas recorrentes',
    'sintomas de elevador degradado',
  ],
  '/erros-elevadores': [
    'erros frequentes em elevadores',
    'códigos de erro do elevador',
    'falhas reportadas pelo quadro',
    'interpretar erros técnicos',
  ],

  // ═══════════════════════════════════════════════════════════════════
  // CLUSTER 8 — COMERCIAL & PRICING
  // ═══════════════════════════════════════════════════════════════════
  '/quanto-custa-auditoria-tecnica-elevadores': [
    'quanto custa uma auditoria técnica',
    'preço de auditoria a elevadores',
    'orçamento de auditoria independente',
    'tabela de preços de auditoria',
  ],
  '/contactos': [
    'falar com a ElevAdvisor',
    'pedir contacto técnico',
    'iniciar conversa com engenheiro',
    'solicitar apoio especializado',
  ],
};

/**
 * Default fallbacks por padrão de URL — usado quando não há entrada explícita.
 */
const FALLBACK_PATTERNS: Array<{ test: RegExp; variants: (slug: string) => string[] }> = [
  {
    test: /^\/glossario\//,
    variants: (slug) => [
      `termo técnico: ${slug}`,
      `definição de ${slug}`,
      `consultar ${slug} no glossário`,
    ],
  },
];

function humanizeSlug(url: string): string {
  return url
    .replace(/^\//, '')
    .split('/')
    .pop()!
    .replace(/-/g, ' ');
}

/**
 * Devolve um anchor variado para uma URL interna.
 *
 * @param url — caminho interno (ex: "/auditoria-elevadores")
 * @param seed — pista determinística (índice do link na página, ID do componente).
 *               Se omitido, usa hash da URL.
 * @param used — anchors já usados nesta página, para evitar repetição.
 */
export function pickAnchor(url: string, seed?: number | string, used?: Set<string>): string {
  const variants = getVariants(url);
  if (variants.length === 0) return humanizeSlug(url);

  // Filtrar usados
  const available = used ? variants.filter((v) => !used.has(v)) : variants;
  const pool = available.length > 0 ? available : variants;

  const index = typeof seed === 'number'
    ? seed % pool.length
    : hashString(seed ?? url) % pool.length;

  const chosen = pool[index];
  used?.add(chosen);
  return chosen;
}

export function getVariants(url: string): string[] {
  const explicit = ANCHOR_VARIATIONS[url];
  if (explicit) return explicit;

  for (const pattern of FALLBACK_PATTERNS) {
    if (pattern.test.test(url)) return pattern.variants(humanizeSlug(url));
  }
  return [];
}

/**
 * Cria um tracker de anchors usados numa página — para passar a vários `pickAnchor`.
 */
export function createAnchorTracker(): Set<string> {
  return new Set<string>();
}

function hashString(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = ((h << 5) - h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/**
 * Lista de anchors genéricos PROIBIDOS — devem ser substituídos por variantes semânticas.
 * Usar em audits para detectar links com baixa qualidade SEO.
 */
export const FORBIDDEN_GENERIC_ANCHORS = [
  'clique aqui',
  'clica aqui',
  'aqui',
  'saiba mais',
  'sabe mais',
  'ver mais',
  'leia mais',
  'continuar a ler',
  'mais informação',
  'mais info',
  'link',
];

export function isGenericAnchor(text: string): boolean {
  const normalized = text.trim().toLowerCase();
  return FORBIDDEN_GENERIC_ANCHORS.includes(normalized);
}
